import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Amoladora from './Amoladora';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false); // Estado necesario para el nuevo botón
  const location = useLocation();

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Productos', href: '/productos' },
    { name: 'Contacto', href: '/contacto' },
    { name: 'Nosotros', href: '/nosotros' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-0" data-testid="logo-link">
              <div className="rounded-sm">
                <Amoladora className="w-12 h-12" />
              </div>

              <div className="flex flex-col">
                <span className="text-2xl font-bold text-slate-900 uppercase tracking-tight">MetalBrix</span>
                <span className="text-xs text-slate-600 uppercase tracking-wider">Soluciones Metalúrgicas</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`text-sm font-medium uppercase tracking-wide transition-colors ${
                    isActive(item.href)
                      ? 'text-blue-700 border-b-2 border-blue-700'
                      : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}

              {/* INTEGRACIÓN DEL DROPDOWN */}
              <div className="relative inline-block text-left">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  data-testid="trabaja-con-nosotros-btn"
                  className="text-sm font-medium uppercase tracking-wide text-slate-700 hover:text-slate-900 transition-colors flex items-center gap-1"
                >
                  Trabaja con nosotros
                  <span className="text-[10px]">▼</span>
                </button>

                {dropdownOpen && (
                  <div 
                    className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 shadow-md py-1 z-50"
                    data-testid="dropdown-menu"
                  >
                    <a
                      href="https://docs.google.com/forms/d/e/1FAIpQLSeb0nvE-2IqQyCiVCgSv78Ibj4EvzYKlpk6s3R_UYmkPI_NHA/viewform?usp=publish-editor"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      data-testid="completa-formulario-link"
                    >
                      Completa el formulario
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-button"
              className="md:hidden p-2 rounded-sm text-slate-700 hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 text-sm font-medium uppercase tracking-wide rounded-sm transition-colors ${
                    isActive(item.href)
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {/* Opción adicional para móvil siguiendo tu estilo de links */}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSeb0nvE-2IqQyCiVCgSv78Ibj4EvzYKlpk6s3R_UYmkPI_NHA/viewform?usp=publish-editor"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium uppercase tracking-wide text-slate-700 hover:bg-slate-100 rounded-sm"
              >
                Trabaja con nosotros
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-0 mb-4">
                <div className="rounded-sm">
                  <Amoladora className="w-12 h-12" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold uppercase tracking-tight">MetalBrix</span>
                  <span className="text-xs text-slate-400 uppercase tracking-wider">Soluciones Metalúrgicas</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Especialistas en trabajos de metalurgia con la más alta calidad y precisión.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold uppercase mb-4 tracking-wide">Enlaces Rápidos</h3>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-slate-400 hover:text-white transition-colors text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
                {/* Link extra en el footer */}
                <li>
                  <a href="https://docs.google.com/forms/d/e/1FAIpQLSeb0nvE-2IqQyCiVCgSv78Ibj4EvzYKlpk6s3R_UYmkPI_NHA/viewform?usp=publish-editor" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors text-sm">
                    Trabaja con nosotros
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold uppercase mb-4 tracking-wide">Contacto</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li> Av. Paula Albarracín de Sarmiento 2085 - Rivadavia, San Juan, Argentina</li><br />
                <li>Tel: 2644762543</li>
                <li>Email: metalbrix2025@gmail.com</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-400 text-sm">
            <p>&copy; {new Date().getFullYear()} MetalBrix. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;